import * as ImagePicker from "expo-image-picker";

import { Linking, Platform, findNodeHandle } from "react-native";
import React, { useCallback, useRef } from "react";

import { useActionSheet } from "@expo/react-native-action-sheet";
import { useIntl } from "react-intl";
import {
  addBreadcrumb,
  captureException,
  captureMessage,
} from "../../common/sentry";
import { useAlert } from "../../common/Alert";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const openAppSettings = () => {
  Linking.openSettings();
};

export const useImageActionSheet = ({
  onAddImage,
  allowsEditing = true,
  onRemoveImage,
  options,
}: {
  onAddImage: (
    uri: string,
    media: {
      uri: string;
      width: number;
      height: number;
    },
    type?: "video" | "image"
  ) => void;
  options?: ImagePicker.ImagePickerOptions;
  onRemoveImage?(): void;
  allowsEditing?: boolean;
}) => {
  const intl = useIntl();
  const { showActionSheetWithOptions } = useActionSheet();
  const anchorRef = useRef<
    | number
    | React.Component<any, any, any>
    | React.ComponentClass<any, any>
    | null
  >(null);
  const { openCamera } = useOpenCamera({ onAddImage, options });
  const insets = useSafeAreaInsets();
  const { openImagePicker } = useOpenImagePicker({ onAddImage, options });

  const open = useCallback(async () => {
    if (Platform.OS === "web") {
      await openImagePicker();
      return;
    }

    const anchor: number | undefined =
      Platform.OS === "ios" && anchorRef.current
        ? (findNodeHandle(anchorRef.current ?? null) ?? undefined)
        : undefined;

    showActionSheetWithOptions(
      {
        options: [
          intl.formatMessage({
            defaultMessage: "Cancel",
            description: "useImageActionSheet: cancel option",
          }),
          intl.formatMessage({
            defaultMessage: "Image Library",
            description: "useImageActionSheet: image library option",
          }),
          intl.formatMessage({
            defaultMessage: "Take Picture",
            description: "useImageActionSheet: take picture option",
          }),
          ...(onRemoveImage
            ? [
                intl.formatMessage({
                  defaultMessage: "Remove Image",
                  description: "useImageActionSheet: remove image option",
                }),
              ]
            : []),
        ],
        cancelButtonIndex: 0,
        destructiveButtonIndex: 3,
        anchor,
        containerStyle:
          Platform.OS === "android"
            ? {
                paddingBottom: insets.bottom,
              }
            : undefined,
      },
      async (index: number | undefined) => {
        try {
          switch (index) {
            case 0:
              return;
            case 1: {
              await openImagePicker();
              return;
            }
            case 2: {
              await openCamera();
              return;
            }
            case 3: {
              onRemoveImage?.();
              return;
            }
            default:
              throw new Error(
                "useImageActionSheet Index not implemented" + index
              );
          }
        } catch (e) {
          captureException(e);
        }
      }
    );
  }, [
    insets.bottom,
    intl,
    onRemoveImage,
    openCamera,
    openImagePicker,
    showActionSheetWithOptions,
  ]);
  return [open, anchorRef] as [typeof open, typeof anchorRef];
};

type useOpenImagePickerProps = {
  onAddImage: (
    uri: string,
    media: {
      uri: string;
      width: number;
      height: number;
    },
    type?: "video" | "image"
  ) => void;
  options?: ImagePicker.ImagePickerOptions;
};

export function useOpenImagePicker({
  onAddImage,
  options,
}: useOpenImagePickerProps) {
  const intl = useIntl();

  const Alert = useAlert();
  const [loadingImage, _setLoadingImage] = React.useState(false);
  const setLoadingImage = (value: boolean) => {
    if (Platform.OS === "web") {
      return;
    }
    _setLoadingImage(value);
  };
  const openImagePicker = useCallback(async () => {
    const { granted, canAskAgain, expires, status, accessPrivileges } =
      await ImagePicker.requestMediaLibraryPermissionsAsync();

    addBreadcrumb({
      category: "permission",
      message: "Request media library permission",
      data: {
        granted,
        expires,
        status,
        canAskAgain,
        accessPrivileges,
      },
    });
    if (!granted) {
      captureMessage("Image picker access not granted");
      Alert.alert(
        intl.formatMessage({
          defaultMessage: "Photos access required",
          description: "Missing photos permission alert title",
        }),
        intl.formatMessage({
          defaultMessage: "Let Storesprint access Photos",
          description: "Missing photos permission alert description",
        }),
        [
          {
            onPress: openAppSettings,
            text: intl.formatMessage({
              defaultMessage: "Enabled Photo Access",
              description:
                "Missing photos permission alert enable photo acess option",
            }),
            style: "default",
          },
          {
            text: intl.formatMessage({
              defaultMessage: "Cancel",
              description: "Missing photos permission alert cancel option",
            }),
            style: "cancel",
          },
        ]
      );
    }
    try {
      setLoadingImage(true);
      const result = await ImagePicker.launchImageLibraryAsync({
        allowsMultipleSelection: false,
        allowsEditing: false,
        mediaTypes: ["images"],
        exif: true,
        quality: 1,
        ...options,
      });

      if (
        result.canceled === false &&
        result.assets.length > 0 &&
        result.assets[0].uri
      ) {
        const t =
          result.assets[0].type === "image"
            ? "image"
            : result.assets[0].type === "video"
              ? "video"
              : undefined;
        onAddImage(
          result.assets[0].uri,
          {
            uri: result.assets[0].uri,
            width: result.assets[0].width,
            height: result.assets[0].height,
          },
          t
        );
      }
    } catch (e) {
      captureException(e);
      Alert.alert("Error", "Could not access camera library");
    }
    setLoadingImage(false);
  }, [Alert, intl, onAddImage, options]);

  return { openImagePicker, isLoading: loadingImage };
}

export function useOpenImagePickerWithPromise({
  options,
}: {
  options?: ImagePicker.ImagePickerOptions;
}) {
  const intl = useIntl();

  const Alert = useAlert();

  const openImagePicker = useCallback(async () => {
    const { granted, canAskAgain, expires, status, accessPrivileges } =
      await ImagePicker.requestMediaLibraryPermissionsAsync();

    addBreadcrumb({
      category: "permission",
      message: "Request media library permission",
      data: {
        granted,
        expires,
        status,
        canAskAgain,
        accessPrivileges,
      },
    });
    if (!granted) {
      captureMessage("Image picker access not granted");
      Alert.alert(
        intl.formatMessage({
          defaultMessage: "Photos access required",
          description: "Missing photos permission alert title",
        }),
        intl.formatMessage({
          defaultMessage: "Let Storesprint access Photos",
          description: "Missing photos permission alert description",
        }),
        [
          {
            onPress: openAppSettings,
            text: intl.formatMessage({
              defaultMessage: "Enabled Photo Access",
              description:
                "Missing photos permission alert enable photo acess option",
            }),
            style: "default",
          },
          {
            text: intl.formatMessage({
              defaultMessage: "Cancel",
              description: "Missing photos permission alert cancel option",
            }),
            style: "cancel",
          },
        ]
      );
    }
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        allowsMultipleSelection: false,
        allowsEditing: false,
        mediaTypes: ["images"],
        exif: true,
        quality: 1,
        ...options,
      });

      if (
        result.canceled === false &&
        result.assets.length > 0 &&
        result.assets[0].uri
      ) {
        const t =
          result.assets[0].type === "image"
            ? "image"
            : result.assets[0].type === "video"
              ? "video"
              : undefined;
        return {
          uri: result.assets[0].uri,
          width: result.assets[0].width,
          height: result.assets[0].height,
          type: t,
        };
      }
    } catch (e) {
      captureException(e);
      Alert.alert("Error", "Could not access camera library");
    }
  }, [Alert, intl, options]);

  return openImagePicker;
}

export function useOpenCamera({
  onAddImage,
  options,
}: useOpenImagePickerProps) {
  const intl = useIntl();
  const [loadingImage, _setLoadingImage] = React.useState(false);
  const setLoadingImage = (value: boolean) => {
    if (Platform.OS === "web") {
      return;
    }
    _setLoadingImage(value);
  };
  const Alert = useAlert();
  const openCamera = useCallback(async () => {
    const { granted, expires, status, canAskAgain } =
      await ImagePicker.requestCameraPermissionsAsync();

    addBreadcrumb({
      category: "permission",
      message: "Request camera permission",
      data: {
        granted,
        expires,
        status,
        canAskAgain,
      },
    });
    if (!granted) {
      captureMessage("Image access not granted");
      Alert.alert(
        intl.formatMessage({
          defaultMessage: "Camera access required",
          description: "Missing camera permission alert title",
        }),
        intl.formatMessage({
          defaultMessage: "Let Storesprint access Camera",
          description: "Missing camera permission alert description",
        }),
        [
          {
            onPress: openAppSettings,
            text: intl.formatMessage({
              defaultMessage: "Enable Camera Access",
              description:
                "Missing camera permission alert enable camera access option",
            }),
            style: "default",
          },
          {
            text: intl.formatMessage({
              defaultMessage: "Cancel",
              description: "Missing camera permission alert cancel option",
            }),
            style: "cancel",
          },
        ]
      );
    } else {
      try {
        setLoadingImage(true);
        const result = await ImagePicker.launchCameraAsync({
          allowsMultipleSelection: false,
          mediaTypes: ["images"],
          quality: 1,
          ...options,
        });

        if (
          result.canceled === false &&
          result.assets.length > 0 &&
          result.assets[0].uri
        ) {
          const t =
            result.assets[0].type === "image"
              ? "image"
              : result.assets[0].type === "video"
                ? "video"
                : undefined;
          onAddImage(
            result.assets[0].uri,
            {
              uri: result.assets[0].uri,
              width: result.assets[0].width,
              height: result.assets[0].height,
            },
            t
          );
        }
      } catch (e) {
        captureException(e);
        Alert.alert(
          "Error",
          "Could not access camera, is your device out of Storage?  "
        );
      }
      setLoadingImage(false);
    }
  }, [Alert, intl, onAddImage, options]);

  return { openCamera, isLoading: loadingImage };
}

export function useOpenCameraWithPromise({
  options,
}: {
  options?: ImagePicker.ImagePickerOptions;
}) {
  const intl = useIntl();

  const Alert = useAlert();
  const openCamera = useCallback(async () => {
    const { granted, expires, status, canAskAgain } =
      await ImagePicker.requestCameraPermissionsAsync();

    addBreadcrumb({
      category: "permission",
      message: "Request camera permission",
      data: {
        granted,
        expires,
        status,
        canAskAgain,
      },
    });
    if (!granted) {
      captureMessage("Image access not granted");
      Alert.alert(
        intl.formatMessage({
          defaultMessage: "Camera access required",
          description: "Missing camera permission alert title",
        }),
        intl.formatMessage({
          defaultMessage: "Let Storesprint access Camera",
          description: "Missing camera permission alert description",
        }),
        [
          {
            onPress: openAppSettings,
            text: intl.formatMessage({
              defaultMessage: "Enable Camera Access",
              description:
                "Missing camera permission alert enable camera access option",
            }),
            style: "default",
          },
          {
            text: intl.formatMessage({
              defaultMessage: "Cancel",
              description: "Missing camera permission alert cancel option",
            }),
            style: "cancel",
          },
        ]
      );
    } else {
      try {
        const result = await ImagePicker.launchCameraAsync({
          allowsMultipleSelection: false,
          mediaTypes: ["images"],
          quality: 1,
          ...options,
        });

        if (
          result.canceled === false &&
          result.assets.length > 0 &&
          result.assets[0].uri
        ) {
          const t =
            result.assets[0].type === "image"
              ? "image"
              : result.assets[0].type === "video"
                ? "video"
                : undefined;
          return {
            uri: result.assets[0].uri,
            width: result.assets[0].width,
            height: result.assets[0].height,
            type: t,
          };
        }
      } catch (e) {
        captureException(e);
        Alert.alert(
          "Error",
          "Could not access camera, is your device out of Storage?  "
        );
      }
    }
  }, [Alert, intl, options]);

  return openCamera;
}
